import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";

const HOSPITALITY_IMG = "https://images.unsplash.com/photo-1777245398213-e95bb34c442a?q=85&w=2400";
const RETAIL_IMG = "https://images.pexels.com/photos/18206310/pexels-photo-18206310.jpeg?w=2400";

const HOSP = ["Hotel Development", "Restaurant Planning", "Resort Consultancy", "Brand & Operator Onboarding"];
const RETAIL = ["Mall Design", "Store Design", "Retail Expansion", "Commercial Planning"];

export default function HospitalityRetail() {
  return (
    <section className="relative bg-white py-24 sm:py-32" data-testid="hospitality-retail-section">
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="overline text-gold">Our distinctive USP</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-navy mt-4">
            Where engineering meets <span className="italic font-light text-emerald-brand">experience.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-brand leading-relaxed">
            NEPHRC&apos;s hospitality and retail practices set us apart — the rare consultancy that engineers buildings as carefully as it engineers experiences and revenue.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden bg-navy text-white group"
            data-testid="hospitality-card"
          >
            <div className="absolute inset-0">
              <img src={HOSPITALITY_IMG} alt="Hospitality" loading="lazy" className="w-full h-full object-cover opacity-50 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
            </div>
            <div className="relative p-8 sm:p-12 min-h-[560px] flex flex-col justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-gold" />
                <span className="overline text-gold">Hospitality</span>
              </div>
              <div>
                <h3 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Hotels &amp; resorts that operate at world standards.
                </h3>
                <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                  {HOSP.map((h) => (
                    <li key={h} className="flex items-center gap-3 text-sm text-white/85 border-l border-gold/60 pl-3">
                      {h}
                    </li>
                  ))}
                </ul>
                <Link to="/services/hospitality-consultancy" data-testid="hospitality-cta" className="mt-10 inline-flex items-center gap-2 text-gold font-semibold text-xs tracking-[0.2em] uppercase border-b border-gold pb-1">
                  Explore hospitality <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative overflow-hidden bg-emerald-brand text-white group"
            data-testid="retail-card"
          >
            <div className="absolute inset-0">
              <img src={RETAIL_IMG} alt="Retail" loading="lazy" className="w-full h-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-brand via-emerald-brand/70 to-emerald-brand/40" />
            </div>
            <div className="relative p-8 sm:p-12 min-h-[560px] flex flex-col justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-gold" />
                <span className="overline text-gold">Retail</span>
              </div>
              <div>
                <h3 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Retail destinations that out-perform their catchment.
                </h3>
                <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                  {RETAIL.map((h) => (
                    <li key={h} className="flex items-center gap-3 text-sm text-white/90 border-l border-gold/60 pl-3">
                      {h}
                    </li>
                  ))}
                </ul>
                <Link to="/services/retail-consultancy" data-testid="retail-cta" className="mt-10 inline-flex items-center gap-2 text-gold font-semibold text-xs tracking-[0.2em] uppercase border-b border-gold pb-1">
                  Explore retail <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
